// @flow

import Promise from 'bluebird';
import React from 'react';
// $FlowFixMe: `hydrate` will be added in future version of flow
import { hydrate } from 'react-dom';
import { AppContainer } from 'react-hot-loader';
import { Router } from 'react-router-dom';
import { matchRoutes, renderRoutes } from 'react-router-config';
import { Provider } from 'react-redux';
import { trigger } from 'redial';
import lowercaseKeys from 'lowercase-keys';
import { get, isEmpty } from 'lodash';
import {
  stringify as stringifyQueryString,
  parse as parseQueryString
} from 'query-string';
import { parse as parseCookies } from 'cookie';
import { isAuthError } from '@seek/ca-http-client';
import { updateAuthenticated } from 'src/store/user/user';
import createStore from 'src/store/createStore';
import { createAnalytics } from '@seek/seek-jobs-analytics';
import { locationChanged, history } from 'src/store/location/location';
import { environment, useHotLoader } from 'src/config';
import appRoutesConfig from './routes';
import {
  startTransaction,
  endTransaction
} from 'src/modules/redux-batched-subscribe-transaction';
import { getTransaction } from 'src/store/transactions';
import scrollRestoration from 'src/modules/scroll-restoration';
import { setUserAgent } from 'src/modules/chalice-user-agent/isomorphic-useragent';
import { logErrorToRaygun } from 'src/modules/raygun-logger';
import getLocale from 'src/modules/get-locale';
import {
  jobDetailsPageRegex,
  searchResultsPageRegex
} from 'src/modules/routes-regexp';

import type {
  Route,
  Location,
  LocationShape,
  HistoryAction
} from 'react-router';

// Used to calculate 'routeEnter' values for each hook:
let prevRouteHandlers = [];

const browserHistory = history;

scrollRestoration(browserHistory);

setUserAgent(window.navigator.userAgent);

const routerLocationToUrlLocation = ({
  search = '',
  ...location
}: Location): UrlLocation => ({
  ...location,
  query: parseQueryString(search)
});

const urlLocationToRouterLocation = ({
  query = {},
  ...location
}: UrlLocation): LocationShape => ({
  ...location,
  search: stringifyQueryString(query)
});

const fetchDataForRoutes = ({
  matchedRoutes,
  location,
  dispatch,
  authenticated,
  getState
}: {
  matchedRoutes: Array<Route>,
  location: UrlLocation,
  dispatch: *,
  authenticated: boolean,
  getState: *
}) => {
  // Keep a local copy of the previous route handlers array.
  // This is so the 'prevRouteHandlers' array can be safely updated mid-lifecycle.
  const prevRouteHandlersCopy = [...prevRouteHandlers];

  const lowercasedQuery = lowercaseKeys(location.query);

  const handleIntent = (intentName: string, callback: *): void => {
    const { [intentName]: value, ...query } = lowercasedQuery;

    if (typeof value === 'undefined') {
      return;
    }

    browserHistory.replace(urlLocationToRouterLocation({ ...location, query }));

    // Optimisations in React 16 reduced validation of SSR content,
    // which can lead to subtle diffs between the virtual dom and,
    // the browser when the client side re-hydrates. They discuss
    // the issue here: https://github.com/facebook/react/issues/10591.
    // For now, pushing the `callback` to the next tick forces React
    // to see honour the state change rather than right it off as
    // an SSR vs CSR diff.
    setTimeout(() => callback(value), 0);
  };

  const getLocals = routeHandler => ({
    routeEnter: prevRouteHandlersCopy.indexOf(routeHandler) === -1,
    path: location.pathname,
    query: lowercasedQuery,
    state: get(location, 'state', {}),
    locale: getLocale(window.location.hostname),
    referrer: window.document.referrer,
    cookies: parseCookies(window.document.cookie),
    userAgent: window.navigator.userAgent,
    authenticated,
    handleIntent,
    dispatch,
    getState
  });

  const routeHandlers = matchedRoutes.map(route =>
    get(route, 'component', null)
  );
  prevRouteHandlers = routeHandlers;

  const makeTrigger = name => () => trigger(name, routeHandlers, getLocals);

  let triggerFetch = () => Promise.resolve();
  let triggerSeo = () => Promise.resolve();

  if (window.SEEK_REDUX_DATA) {
    delete window.SEEK_REDUX_DATA;
  } else {
    triggerFetch = makeTrigger('fetch');
    triggerSeo = makeTrigger('seo');
  }

  const triggerFirst = makeTrigger('first');
  const triggerAuthenticated = makeTrigger('authenticated');
  const triggerDefer = makeTrigger('defer');
  const triggerMashup = makeTrigger('mashup');
  const triggerPageLoad = makeTrigger('pageLoad');

  const triggerAuthFlow = () =>
    triggerAuthenticated().catch(e => {
      if (isAuthError(e)) {
        dispatch(updateAuthenticated({ authenticated: false }));
      } else {
        logErrorToRaygun({
          error: new Error('Authenticated trigger failed with an error'),
          customData: { error: e, status: get(e, 'response.status') }
        });
      }
    });

  const triggerServerFlow = () => triggerFetch().then(triggerSeo);

  const routeChangeTransaction = getTransaction('routeChange');
  dispatch(startTransaction(routeChangeTransaction));

  Promise.join(
    triggerFirst(),
    authenticated && triggerAuthFlow(),
    triggerServerFlow(),
    triggerDefer()
  )
    .then(triggerMashup)
    .catch(Promise.CancellationError, () => {})
    .finally(triggerPageLoad)
    .then(() => dispatch(endTransaction(routeChangeTransaction)));
};

export default (element: any): void => {
  const dataLayer = window.SK_DL || {};
  const analytics = createAnalytics({ environment }, dataLayer);
  const store = createStore({ analytics }, window.SEEK_REDUX_DATA);

  const { dispatch } = store;

  if (window.SK_DL) {
    delete window.SK_DL;
  }

  const onLocationChanged = (
    location: Location,
    action?: HistoryAction
  ): void => {
    const nextLocation = routerLocationToUrlLocation(location);

    if (action === 'REPLACE') {
      return;
    }
    const { location: currentLocation } = store.getState();
    const {
      pathname: currentPathname,
      query: { page: currentPageNumber } = {}
    } = currentLocation;
    const {
      pathname: nextPathname,
      query: { page: nextPageNumber } = {}
    } = nextLocation;

    const isJobDetails = jobDetailsPageRegex.test(nextPathname);

    const didPageChange =
      currentPathname !== nextPathname ||
      currentPageNumber !== nextPageNumber ||
      isJobDetails;

    if (action !== 'POP' && didPageChange) {
      window.document.body.scrollIntoView();
    }

    const matchedRoutes = matchRoutes(appRoutesConfig, nextPathname).map(
      ({ route }) => route
    );
    const {
      user: { authenticated },
      search
    } = store.getState();
    dispatch(locationChanged(window.location.href, nextLocation));

    const isBackToSerpFromJdp =
      action === 'POP' &&
      jobDetailsPageRegex.test(currentPathname) &&
      searchResultsPageRegex.test(nextPathname);

    if (!isBackToSerpFromJdp || isEmpty(search.refinements)) {
      fetchDataForRoutes({
        matchedRoutes,
        location: nextLocation,
        dispatch,
        authenticated,
        getState: store.getState
      });
    }
  };

  browserHistory.listen(onLocationChanged);

  const wrapHotLoader = app =>
    useHotLoader ? <AppContainer>{app}</AppContainer> : app;

  function renderApp(appRoutes: Array<Route>): void {
    hydrate(
      wrapHotLoader(
        <Provider store={store}>
          <Router history={browserHistory}>{renderRoutes(appRoutes)}</Router>
        </Provider>
      ),
      element
    );
  }

  onLocationChanged(browserHistory.location);

  renderApp(appRoutesConfig);

  if (useHotLoader && get(module, 'hot')) {
    // $FlowFixMe - Warning: Webpack HMR requires this to be in this file.
    module.hot.accept('./routes.js', () => {
      const newRoutes = require('./routes.js').default;
      renderApp(newRoutes);
    });
  }
};
